import React, { useEffect, useRef, ReactNode, MouseEvent } from "react";
import { _cs } from "@togglecorp/fujs";

import Haze from "../Haze";
import Portal from "../Portal";

import styles from "./styles.module.scss";

export interface ModalProps {
  className?: string;
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  onClose?: (reason: { outsideClick?: boolean; escape?: boolean }) => void;
  children?: ReactNode;
}

const ESCAPE_KEY = 27;

const Modal: React.FC<ModalProps> = ({
  className = "",
  closeOnEscape = false,
  closeOnOutsideClick = false,
  onClose = () => {},
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent | globalThis.MouseEvent) => {
    if (!closeOnOutsideClick) return;
    if (!wrapperRef.current) return;

    if (!wrapperRef.current.contains(event.target as Node)) {
      onClose({ outsideClick: true });
    }
  };

  const handleKeyPressed = (event: KeyboardEvent) => {
    if (!closeOnEscape || event.keyCode !== ESCAPE_KEY) return;

    const container = wrapperRef.current;
    const isLastModal = container?.dataset.lastModal === "true";

    if (isLastModal) {
      onClose({ escape: true });
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyPressed);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPressed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalClass = _cs(className, styles.modal, "modal");

  return (
    <Portal>
      <Haze>
        <div ref={wrapperRef} className={modalClass}>
          {children}
        </div>
      </Haze>
    </Portal>
  );
};

export default Modal;
